/**
 * TopToolbar.js — V2 top-left toolbar.
 *
 * Four SQUARE ICON buttons (no text — tooltips carry the labels):
 *   FILE            — New / Save / Save As… / Load / Load from Public
 *   ADD PLAYER HOME — switch team + a row of FORMATION shapes + list of home
 *                     players to place
 *   ADD PLAYER AWAY — switch team + FORMATION shapes + list of away players
 *   ADD ITEM        — Ball / Cone / Target / Zone to place
 *
 * Selecting a player or item dismisses the dropdown and returns a
 * "place" intent via onPlacePlayer(side, index) / onPlaceItem(type). Choosing a
 * formation returns onPlaceFormation(side, key) and lays the whole squad out in
 * one go — the eleven-hand-taps version of the same job.
 */

import { useState, useRef, useEffect } from 'react';
import { useI18n } from './i18n';
import { getTeamLogoUrl, getPlayerPhotoUrl } from './api';
import { getRuntimeAdapter, isMobileBuild } from './platform';
import { FORMATION_KEYS, formationLabel } from './formations';

/* True when a team/player has an UPLOADED image (vs. the placeholder fallback). */
const isUploaded = (path) => !!(path && path.startsWith('/uploads/'));

/* Which ADD-menu entry armed placement, so the toolbar button can name the
   thing that is armed IN THE COACH'S LANGUAGE. It used to carry its own map of
   seven English nouns here — a second, worse copy of the labels the menu
   already translates, and one that never changed language. */
const ARMED_LABEL_KEY = {
  ball: 'add.ball',
  cone: 'add.cone',
  target: 'add.target',
  zone: 'add.polyZone',
  'zone-static': 'add.polyZone',
  'zone-circle': 'add.circleZone',
  'zone-dynamic': 'add.dynamicZone',
};

/* ── Icon button chrome (square, touch-friendly) ── */
const btnBase = {
  width: '42px',
  height: '42px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(15, 23, 42, 0.92)',
  color: '#e2e8f0',
  border: '1px solid #475569',
  borderRadius: '8px',
  cursor: 'pointer',
  touchAction: 'manipulation',
  userSelect: 'none',
  transition: 'background 0.15s, border-color 0.15s',
};

const menuStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  minWidth: '190px',
  // Hard cap so no menu can ever overflow the screen width, no matter the
  // content (long names, translations, etc). Items ellipsize inside.
  maxWidth: 'calc(100vw - 24px)',
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '8px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
  zIndex: 300,
  overflowX: 'hidden',
  overflowY: 'auto', // menus must scroll when taller than the viewport (mobile landscape)
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
  display: 'flex',
  flexDirection: 'column',
  // Never taller than the VISIBLE viewport: uses the dvh-backed variable so
  // the bottom never lands under the mobile browser chrome (landscape).
  maxHeight: 'calc(var(--kv-viewport-h) - 72px)',
};

const menuItemStyle = {
  background: 'none',
  border: 'none',
  color: '#e2e8f0',
  textAlign: 'left',
  padding: '8px 14px',
  fontSize: '12.5px',
  cursor: 'pointer',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  // Single-line rows with a consistent height — stops labels wrapping and
  // rows squashing to different heights (cramped look on mobile).
  whiteSpace: 'nowrap',
  lineHeight: '1.4',
  minHeight: 34,
  minWidth: 0,
};

const storeBadgeStyle = {
  height: 40,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

/* ── Icons (stroke-based SVGs, match the app style) ── */
const svg = (size, children) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const ICON_FILE = svg(20, (
  <>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </>
));

const ICON_PLAYER = svg(20, (
  <>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M4.5 20c1.4-3.5 4.2-5 7.5-5s6.1 1.5 7.5 5" />
  </>
));

const ICON_ITEM = svg(20, (
  <>
    <path d="M12 5v14M5 12h14" />
  </>
));

/* Remove item — mirrors ICON_ITEM but horizontal bar only (the '−'). */
const ICON_REMOVE_ITEM = svg(20, (
  <>
    <path d="M5 12h14" />
  </>
));

export default function TopToolbar({
  homeTeam, awayTeam,
  account = null,
  isPremium = false,
  monthlyPrice = null,   // what Premium costs, from /api/config — the menu says
                         // it so a coach knows the price BEFORE tapping Upgrade
  onFileAction,        // (action: 'new'|'save'|'saveAs'|'load'|'loadPublic') => void
  onPlacePlayer,       // (side: 'home'|'away', index: number) => void
  // PLACE A FORMATION — the row of shapes at the top of the player menu.
  // (side, key) => void; the shapes themselves live in formations.js, so the
  // toolbar draws them and never keeps its own copy of which ones exist.
  onPlaceFormation = null,
  onPlaceItem,         // (type: 'ball'|'cone'|'target'|'zone') => void
  pendingType = null,  // armed placement type — the trigger GLOWS while armed,
                       // so "why is my cursor a crosshair?" answers itself
  placingSide = null,  // 'home' | 'away' while a PLAYER is armed for placement
  placingIndex = -1,   // roster index of that player (✓ in the menu)
  onSwitchTeam,        // (side: 'home'|'away') => void
  onRemoveSelectedItem = null, // () => void — removes the currently selected item from the scenario
  hasSelectedItem = false,     // enables/highlights the remove button
  onDuplicateItem = null,      // () => void — '⧉ Duplicate' in the Add Item menu
  canDuplicateItem = false,    // an item (not a player) is selected
  // LINE LAYOUT — repeat a measured spacing (the D / ⌥D / ⇧D / ⌥⇧D verbs).
  // lineState = { last, step, canMirror }: what the placement trail allows now.
  onPlaceInLine = null,        // (mode: 'step'|'turn'|'mirrorX'|'mirrorZ') => void
  lineState = { last: null, step: null, canMirror: false },
  onLogin = null,      // () => void — opens the login modal
  onLogout = null,     // () => void
  onFeedback = null,   // () => void — opens the feedback dialog
}) {
  const [openMenu, setOpenMenu] = useState(null); // 'file' | 'home' | 'away' | 'item' | null
  const [publicAvailable, setPublicAvailable] = useState(() => !isMobileBuild || getRuntimeAdapter()?.getPublicAvailability?.() || false);
  useEffect(() => {
    if (isMobileBuild) return getRuntimeAdapter()?.onPublicAvailability?.(setPublicAvailable);
  }, []);
  const wrapRef = useRef(null);
  const { t, lang, setLang, langs, price: money } = useI18n();
  // One formatted price for every label in this file; null when the backend has
  // not answered, which every call site treats as "show no number".
  const price = money(monthlyPrice);

  // Close dropdown on outside click
  useEffect(() => {
    if (!openMenu) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('pointerdown', onDocClick);
    return () => document.removeEventListener('pointerdown', onDocClick);
  }, [openMenu]);

  const toggle = (menu) => setOpenMenu(prev => prev === menu ? null : menu);

  const pick = (fn) => {
    setOpenMenu(null);
    fn();
  };

  // ── PLACE A FORMATION ──
  // The five shapes sit ABOVE the player list, because that is the order the
  // work happens in: a coach opens this menu to build a team, and laying out
  // eleven players by hand is the slow part. One tap puts the whole squad into
  // the shape on that side's half; the player list underneath is still there for
  // the one or two tweaks after it.
  const formationRow = (side) => {
    if (!onPlaceFormation || !FORMATION_KEYS.length) return null;
    return (
      <div
        role="group"
        aria-label={t('form.group')}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: '4px',
          padding: '4px 0 6px', borderBottom: '1px solid #334155',
        }}
      >
        {FORMATION_KEYS.map((k) => (
          <button
            key={k}
            type="button"
            title={t('form.place', { name: formationLabel(k) })}
            onClick={() => pick(() => onPlaceFormation(side, k))}
            style={{
              // Small enough that five fit across the menu without widening it —
              // the menu is hard-capped so it can never overflow the screen.
              flex: '1 1 auto', minWidth: '44px',
              fontSize: '11px', fontWeight: '800', padding: '5px 4px',
              borderRadius: '5px', border: '1px solid #334155',
              background: 'rgba(148,163,184,0.10)', color: '#e2e8f0',
              cursor: 'pointer', touchAction: 'manipulation',
            }}
          >
            {formationLabel(k)}
          </button>
        ))}
      </div>
    );
  };

  const playerRows = (side) => {
    const team = side === 'home' ? homeTeam : awayTeam;
    const rows = [];
    if (team?.playerInfo) {
      team.playerInfo.forEach((p, i) => {
        rows.push(
          <button key={`${side}-${i}`} type="button" style={menuItemStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#1e293b'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            onClick={() => pick(() => onPlacePlayer(side, i))}>
            <span style={{
              width: '22px', height: '22px', borderRadius: '4px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              // Side identity colours (home = blue, away = red), matching the
              // team buttons + arrow colours.
              background: side === 'home' ? '#2563eb' : '#dc2626',
              color: '#fff', fontSize: '10px', fontWeight: '800',
            }}>
              {p.number || i + 1}
            </span>
            {/* Fixed-size photo slot rendered for EVERY row — keeps the name
                column aligned whether or not the player has an uploaded photo */}
            <span style={{
              width: 24, height: 24, borderRadius: 4, flexShrink: 0, overflow: 'hidden',
              background: 'rgba(255,255,255,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isUploaded(p.photo) ? (
                <img src={getPlayerPhotoUrl(p)} alt=""
                  style={{ width: 24, height: 24, objectFit: 'cover', display: 'block' }} />
              ) : (
                <span style={{ fontSize: 9, color: '#475569' }}>◦</span>
              )}
            </span>
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              minWidth: 0, flex: '1 1 auto', // flex min-width:0 lets the name shrink + ellipsize instead of widening the menu
            }}>
              {placingSide === side && placingIndex === i ? '✓ ' : ''}{p.name || `Player ${i + 1}`}
            </span>
            <span style={{ color: '#64748b', fontSize: '10px', marginLeft: 'auto', flexShrink: 0 }}>
              {p.position || ''}
            </span>
          </button>
        );
      });
    }
    return rows;
  };

  return (
    <div ref={wrapRef} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
      {/* FILE */}
      <div style={{ position: 'relative' }}>
        <button className="kv-toolbar-trigger" type="button"
          title={t('tb.file')}
          aria-label={t('tb.file')}
          style={{ ...btnBase, background: openMenu === 'file' ? '#1e293b' : btnBase.background, color: '#e2e8f0' }}
          onClick={() => toggle('file')}>{ICON_FILE}</button>
        {openMenu === 'file' && (
          <div className="kv-toolbar-menu" style={{ ...menuStyle, minWidth: 300 }}>
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onFileAction('new'))}>{t('menu.new')}</button>
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onFileAction('save'))}>{t('menu.save')}</button>
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onFileAction('saveAs'))}>{t('menu.saveAs')}</button>
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onFileAction('load'))}>{t('menu.load')}</button>
            <button type="button" disabled={isMobileBuild && !publicAvailable} title={isMobileBuild && !publicAvailable ? t('tb.publicOffline') : undefined} style={{ ...menuItemStyle, ...(isMobileBuild && !publicAvailable ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }} onClick={() => pick(() => isMobileBuild ? getRuntimeAdapter()?.openPublic() : onFileAction('loadPublic'))}>{t('menu.loadPublic')}</button>
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onFileAction('teams'))}>{t('menu.teams')}</button>
            {isMobileBuild && <button type="button" style={menuItemStyle} onClick={() => pick(() => getRuntimeAdapter()?.openSync())}>Sync &amp; offline</button>}
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onFileAction('help'))}>{t('menu.help')}</button>
            <div style={{ height: 1, background: '#334155', margin: '4px 0' }} />
            {account ? (
              <>
                {isMobileBuild ? (
                  <div style={{ ...menuItemStyle, color: '#94a3b8', cursor: 'default', fontSize: '11px' }}>
                    <span style={{ flexShrink: 0 }}>{t('menu.account')}</span>
                    <span style={{
                      color: '#e2e8f0', fontWeight: '700', flex: '1 1 auto', minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right',
                    }}>{account.name}</span>
                    {isPremium ? <span style={{ flexShrink: 0 }}>⭐</span> : null}
                  </div>
                ) : (
                  /* Web: the row opens the in-app Account modal (name, password,
                     subscription) — no more trip to the backend /account page. */
                  <button type="button" style={{ ...menuItemStyle, color: '#94a3b8', fontSize: '11px' }}
                    onClick={() => pick(() => onFileAction('account'))}>
                    <span style={{ flexShrink: 0 }}>{t('menu.account.open')}</span>
                    <span style={{
                      color: '#e2e8f0', fontWeight: '700', flex: '1 1 auto', minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right',
                    }}>{account.name}</span>
                    {isPremium ? <span style={{ flexShrink: 0 }}>⭐</span> : null}
                  </button>
                )}
                {!isPremium && (
                  <button type="button" style={{ ...menuItemStyle, color: '#22c55e', fontWeight: '700' }}
                    onClick={() => pick(() => isMobileBuild ? getRuntimeAdapter()?.openSubscriptions() : onFileAction('upgrade'))}
                    // The price rides with the offer. Until /api/config answers
                    // (offline, guest, backend down) the entry still reads — it
                    // just does not invent a number.
                    >{price ? t('menu.upgradePrice', { price }) : t('menu.upgrade')}</button>
                )}
                <button type="button" style={{ ...menuItemStyle, color: '#ef4444' }}
                  onClick={() => pick(() => isMobileBuild ? getRuntimeAdapter()?.openAccount() : onLogout && onLogout())}>{isMobileBuild ? (getRuntimeAdapter()?.context?.network?.token ? 'Account' : 'Sign in / Register') : t('menu.logout')}</button>
              </>
            ) : (
              <button type="button" style={{ ...menuItemStyle, color: '#22c55e', fontWeight: '700' }}
                onClick={() => pick(() => onLogin && onLogin())}>{t('menu.login')}</button>
            )}
            <button type="button" style={menuItemStyle}
              onClick={() => pick(() => onFeedback && onFeedback())}>{t('fb.title')}</button>
            {/* ── Language select — under the Account entry (per request) ── */}
            <div style={{ height: 1, background: '#334155', margin: '4px 0' }} />
            <div style={{ ...menuItemStyle, color: '#94a3b8', cursor: 'default', fontSize: '11px' }}>
              🌐 {t('menu.language')}
            </div>
            <div style={{ display: 'flex', gap: '4px', padding: '2px 8px 8px' }}>
              {langs.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setLang(l.code)}
                  title={l.name}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    padding: '5px 0', fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: 'pointer',
                    whiteSpace: 'nowrap', // pill content never squishes
                    border: lang === l.code ? '1px solid #3b82f6' : '1px solid #475569',
                    background: lang === l.code ? '#1e3a8a' : '#1e293b',
                    color: lang === l.code ? '#fff' : '#cbd5e1',
                  }}
                >{l.flag} {l.code.split('-')[0].toUpperCase()}</button>
              ))}
            </div>
            {!isMobileBuild && (
              <>
                <div style={{ height: 1, background: '#334155', margin: '4px 0' }} />
                <div style={{ ...menuItemStyle, color: '#94a3b8', cursor: 'default', fontSize: '11px' }}>
                  {t('menu.mobileApps')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px 8px' }}>
                  <a href="https://play.google.com/store/apps/details?id=com.kickviz.mobile"
                    target="_blank" rel="noopener noreferrer" aria-label={t('menu.androidApp')}
                    style={{ ...storeBadgeStyle, width: 156 }}>
                    <img src={`/store-badges/google-play-${lang}.png`} alt={t('menu.androidApp')}
                      style={{ display: 'block', height: lang === 'en' ? 60 : 52, width: 'auto' }} />
                  </a>
                  <span aria-disabled="true" title={t('menu.iosComingSoon')}
                    style={{ ...storeBadgeStyle, width: 120, opacity: 0.45, cursor: 'not-allowed' }}>
                    <img src={`/store-badges/app-store-${lang}.svg`} alt={`${t('menu.iosApp')} — ${t('menu.iosComingSoon')}`}
                      style={{ display: 'block', height: 40, width: 'auto' }} />
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ADD PLAYER HOME — blue identity; shows the team logo when uploaded */}
      <div style={{ position: 'relative' }}>
        <button className="kv-toolbar-trigger" type="button"
          title={placingSide === 'home'
            ? t('tb.placingHome')
            : t('tb.addHome')}
          aria-label={t('tb.addHome')}
          aria-pressed={placingSide === 'home'}
          style={{ ...btnBase, background: openMenu === 'home' ? '#1e293b' : (placingSide === 'home' ? 'rgba(147,197,253,0.28)' : btnBase.background), color: '#93c5fd' }}
          onClick={() => toggle('home')}>
          {isUploaded(homeTeam?.badge)
            ? <img src={getTeamLogoUrl(homeTeam)} alt={t('tb.homeLogo')}
                style={{ width: 26, height: 26, borderRadius: 5, objectFit: 'cover', display: 'block' }} />
            : ICON_PLAYER}
        </button>
        {openMenu === 'home' && (
          <div className="kv-toolbar-menu" style={menuStyle}>
            <button type="button" style={{ ...menuItemStyle, color: '#38bdf8', fontWeight: '700', borderBottom: '1px solid #334155' }}
              onClick={() => pick(() => onSwitchTeam('home'))}>
              ⇄ <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '10px' }}>{homeTeam?.name || ''}</span>
            </button>
            {formationRow('home')}
            {playerRows('home')}
          </div>
        )}
      </div>

      {/* ADD PLAYER AWAY — red identity; shows the team logo when uploaded */}
      <div style={{ position: 'relative' }}>
        <button className="kv-toolbar-trigger" type="button"
          title={placingSide === 'away'
            ? t('tb.placingAway')
            : t('tb.addAway')}
          aria-label={t('tb.addAway')}
          aria-pressed={placingSide === 'away'}
          style={{ ...btnBase, background: openMenu === 'away' ? '#1e293b' : (placingSide === 'away' ? 'rgba(252,165,165,0.28)' : btnBase.background), color: '#fca5a5' }}
          onClick={() => toggle('away')}>
          {isUploaded(awayTeam?.badge)
            ? <img src={getTeamLogoUrl(awayTeam)} alt={t('tb.awayLogo')}
                style={{ width: 26, height: 26, borderRadius: 5, objectFit: 'cover', display: 'block' }} />
            : ICON_PLAYER}
        </button>
        {openMenu === 'away' && (
          <div className="kv-toolbar-menu" style={menuStyle}>
            <button type="button" style={{ ...menuItemStyle, color: '#38bdf8', fontWeight: '700', borderBottom: '1px solid #334155' }}
              onClick={() => pick(() => onSwitchTeam('away'))}>
              <span style={{ flexShrink: 0 }}>⇄</span>
              <span style={{
                marginLeft: 'auto', color: '#64748b', fontSize: '10px', minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{awayTeam?.name || ''}</span>
            </button>
            {formationRow('away')}
            {playerRows('away')}
          </div>
        )}
      </div>

      {/* ADD ITEM */}
      <div style={{ position: 'relative' }}>
        <button className="kv-toolbar-trigger" type="button"
          title={pendingType && ARMED_LABEL_KEY[pendingType]
            ? t('tb.armedItem', { what: t(ARMED_LABEL_KEY[pendingType]) })
            : t('tb.addItem')}
          aria-label={t('tb.addItem')}
          aria-pressed={!!pendingType}
          style={{ ...btnBase, background: openMenu === 'item' ? '#1e293b' : (pendingType ? 'rgba(251,191,36,0.28)' : btnBase.background), color: '#fbbf24' }}
          onClick={() => toggle('item')}>{ICON_ITEM}</button>
        {openMenu === 'item' && (
          <div className="kv-toolbar-menu" style={menuStyle}>
            {/* Each entry names its ⇧-digit twin — arming placement straight
                from the keyboard, then clicking the pitch, skips this menu.
                A ✓ marks the type that is ALREADY armed (clicking it cancels). */}
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onPlaceItem('ball'))}>{pendingType === 'ball' ? '✓ ' : ''}{t('add.ball')}<span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '10px' }}>⇧4</span></button>
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onPlaceItem('cone'))}>{pendingType === 'cone' ? '✓ ' : ''}{t('add.cone')}<span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '10px' }}>⇧1</span></button>
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onPlaceItem('target'))}>{pendingType === 'target' ? '✓ ' : ''}{t('add.target')}<span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '10px' }}>⇧2</span></button>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '4px 6px' }} />
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onPlaceItem('zone-static'))}>{['zone', 'zone-static'].includes(pendingType) ? '✓ ' : ''}{t('add.polyZone')}<span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '10px' }}>⇧3</span></button>
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onPlaceItem('zone-circle'))}>{pendingType === 'zone-circle' ? '✓ ' : ''}{t('add.circleZone')}<span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '10px' }}>⇧5</span></button>
            <button type="button" style={menuItemStyle} onClick={() => pick(() => onPlaceItem('zone-dynamic'))}>{pendingType === 'zone-dynamic' ? '✓ ' : ''}{t('add.dynamicZone')}<span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '10px' }}>⇧6</span></button>
            {/* DUPLICATE THE SELECTED ITEM — the copy verb for a thing you
                already have: a line of cones, a second zone box, another ball.
                It used to be Ctrl+D only. Every item gets it here, because a
                ball and a zone open their OWN verb (attach / shape) on
                long-press, so the item menu is not their path. Players are
                fixed squad slots — they copy/paste instead, so the entry is
                dimmed and says why. */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '4px 6px' }} />
            <button type="button"
              disabled={!canDuplicateItem}
              title={canDuplicateItem
                ? t('tb.duplicateHint')
                : hasSelectedItem
                  ? t('tb.duplicatePlayer')
                  : t('tb.duplicateNeedItem')}
              style={{ ...menuItemStyle, ...(canDuplicateItem ? {} : { opacity: 0.4, cursor: 'not-allowed' }) }}
              onClick={() => pick(() => onDuplicateItem && onDuplicateItem())}>{t('add.duplicate')}<span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '10px' }}>Ctrl+D</span></button>
            {/* LINE LAYOUT — these four verbs (next in line / turn the line /
                mirror to the other half / mirror to the other side) moved INTO
                the item's own Action menu. They used to sit here, enabled by
                whatever was placed LAST, so with a cone in the trail and a
                player selected they still looked live — and tapping one laid a
                cone down while you thought you were working on the player. In
                the item menu the target cannot be ambiguous: the menu belongs
                to the item, and an entry only appears if that item is actually
                in the line. The chords still work (D / ⌥D / ⇧D / ⌥⇧D) and the
                Help sheet lists them. */}
          </div>
        )}
      </div>

      {/* REMOVE SELECTED ITEM — the '−' next to the '+'; removes whatever is
          selected (player / ball / cone / target / zone) from the scenario.
          Dimmed when nothing is selected so the state is always obvious. */}
      <button
        type="button"
        title={hasSelectedItem ? t('tb.removeItem') : t('tb.removeItemFirst')}
        className="kv-toolbar-trigger"
        aria-label={t('tb.removeItem')}
        disabled={!hasSelectedItem}
        style={{
          ...btnBase,
          color: hasSelectedItem ? '#f87171' : '#475569',
          border: hasSelectedItem ? '1px solid #7f1d1d' : btnBase.border,
          opacity: hasSelectedItem ? 1 : 0.55,
          cursor: hasSelectedItem ? 'pointer' : 'not-allowed',
        }}
        onClick={() => onRemoveSelectedItem && onRemoveSelectedItem()}
      >{ICON_REMOVE_ITEM}</button>
    </div>
  );
}
